import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Package, Mail, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { productsAPI, getUploadUrl } from '../services/api';
import { RichTextDisplay } from '../components/ui/rich-text-display';

export const ProductDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await productsAPI.getOne(id);
      setProduct(response.data);
      
      // Load related products (same category)
      const allProductsRes = await productsAPI.getAll();
      const related = allProductsRes.data
        .filter(p => p.category === response.data.category && p.id !== id)
        .slice(0, 3);
      setRelatedProducts(related);
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t('products.noProducts')}</p>
          <Button asChild className="mt-4">
            <Link to="/products">{t('products.backToProducts')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="product-detail-page">
      {/* Hero Section */}
      <section className="relative py-12 bg-gradient-dark overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link 
              to="/products" 
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
              data-testid="back-to-products"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('products.backToProducts')}
            </Link>
            <h1 className="font-display font-bold text-4xl lg:text-5xl text-white">
              {product.name}
            </h1>
            <Badge variant="secondary" className="mt-4">
              {product.category}
            </Badge>
          </motion.div>
        </div>
      </section>

      {/* Product Content */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Card className="glass-card overflow-hidden">
                <div className="aspect-square">
                  <img
                    src={getUploadUrl(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </Card>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('products.description')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RichTextDisplay content={product.description} className="text-muted-foreground" />
                </CardContent>
              </Card>

              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>{t('products.category')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge variant="outline" className="text-base px-4 py-2">
                    {product.category}
                  </Badge>
                </CardContent>
              </Card>

              {/* Contact CTA */}
              <Card className="glass-card bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <h3 className="font-display font-semibold text-lg mb-4">
                    {t('products.contactForInfo')}
                  </h3>
                  <div className="space-y-3">
                    <Button asChild className="w-full">
                      <Link to="/contact">
                        <Mail className="w-4 h-4 mr-2" />
                        {t('contact.send')}
                      </Link>
                    </Button>
                    <div className="flex items-center justify-center text-sm text-muted-foreground">
                      <Phone className="w-4 h-4 mr-2" />
                      comunicazioni@gmed.it
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-20"
            >
              <h2 className="font-display font-bold text-2xl mb-8">
                {t('products.relatedProducts')}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedProducts.map((relatedProduct) => (
                  <Link 
                    key={relatedProduct.id} 
                    to={`/products/${relatedProduct.id}`}
                    className="group"
                  >
                    <Card className="glass-card h-full overflow-hidden">
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={getUploadUrl(relatedProduct.image)}
                          alt={relatedProduct.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                      <CardContent className="pt-4">
                        <h3 className="font-semibold group-hover:text-primary transition-colors">
                          {relatedProduct.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {relatedProduct.description}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
